import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { api, errMsg } from "../lib/api";
import { ProductCard } from "../components/ProductCard";
import { EditorialIngredientsImage } from "../components/EditorialIngredients";
import usePageMeta from "../hooks/usePageMeta";

const STEPS = [
  { n: "01", t: "Choose Your Bottles", d: "Order as many bottles as you like. Functional juices are $12 each with automatic 3-pack ($28) and 6-pack ($54) pricing; Renew+ (traditional) is $10." },
  { n: "02", t: "Choose Your Pickup Day", d: "SandersFit Dallas or South Side on Lamar — Monday, Wednesday & Thursday evenings, Tuesday & Friday all day. Orders need at least 24 hours' notice; your exact time is coordinated after availability is confirmed. DFW delivery available by arrangement." },
  { n: "03", t: "Make It Recurring (optional)", d: "Set a weekly (every 7 days) or every-14-days pickup. Skip, pause or cancel online anytime." },
];

const FAQS = [
  { q: "Which juice contains creatine?", a: "Flow+ is the juice listed with added creatine: beet, red apple, lemon and ginger. Creatine supports short, intense activity when consistently consumed in effective amounts — per-bottle amounts are pending owner confirmation." },
  { q: "How does pickup work?", a: "Choose SandersFit Dallas or South Side on Lamar and a pickup day at checkout — Mon/Wed/Thu evenings or Tue/Fri all day. Orders need at least 24 hours' notice, and pickup is always subject to availability and confirmed scheduling — we coordinate your exact time after your order is confirmed. DFW delivery is available by arrangement." },
  { q: "Can I order juice in bulk for an event or team?", a: "Yes — bulk orders of 36 bottles are $360 (reference pricing) by request. They need at least 48 hours' notice and confirmed pickup or delivery availability, so we take them as inquiries rather than instant checkout. Send a bulk request from the Build Your Stack page." },
  { q: "How fresh are the juices?", a: "Made fresh in small batches. Owner's guidance: best enjoyed within 3–4 days refrigerated; may be frozen up to 3–4 months — thaw in the refrigerator, enjoy promptly, and don't refreeze." },
];

export default function Home() {
  usePageMeta("Cold-Pressed & Functional Juices in Dallas | Tiff Wellness Co.", "Fresh. Functional. Intentional. Cold-pressed juices crafted in Dallas to complement your movement, nutrition and wellness goals — DFW pickup and delivery by arrangement.");
  const [products, setProducts] = useState([]);
  const [email, setEmail] = useState("");
  const [optIn, setOptIn] = useState(false);
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.get("/products").then((r) => setProducts(r.data)); }, []);

  const signup = async (e) => {
    e.preventDefault();
    if (!optIn) { toast.error("Please tick the consent box to join the email list."); return; }
    setBusy(true);
    try {
      await api.post("/leads", { email, source: "homepage_signup", marketing_opt_in: true });
      toast.success("You're on the list. Welcome!");
      setEmail("");
    } catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };

  return (
    <div>
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 pointer-events-none" aria-hidden="true">
          <div className="absolute -top-24 -right-24 w-96 h-96 rounded-full bg-[#F6E3DC]" />
          <div className="absolute bottom-0 -left-32 w-80 h-80 rounded-full bg-[#F6E3DC]/70" />
        </div>
        <div className="relative max-w-6xl mx-auto px-4 pt-14 pb-16 grid lg:grid-cols-2 gap-12 items-center">
        <div className="fade-up">
          <p className="eyebrow mb-4">Dallas, TX · Cold-pressed · Made fresh in small batches</p>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl leading-[1.08]">Fresh. Functional. Intentional.</h1>
          <p className="mt-5 text-base md:text-lg text-[#171715]/75 max-w-xl">Cold-pressed juices thoughtfully crafted to complement your lifestyle, movement, and wellness goals.</p>
          <div className="mt-8 flex flex-wrap gap-3">
            <Link to="/build-your-stack" data-testid="hero-build-stack-button" className="btn-ink">Build Your Stack</Link>
            <Link to="/shop" data-testid="hero-explore-juices-button" className="btn-ghost">Explore the Juices</Link>
          </div>
          <div className="mt-10 hidden sm:flex gap-2" data-testid="hero-bottle-strip">
            {products.slice(0, 6).map((p) => (
              <Link to={`/products/${p.slug}`} key={p.slug} className="block photo-panel p-1.5">
                <img src={p.image.replace(/-800\.(jpg|png)$/, "-480.webp")} alt={p.image_alt} width="42" height="56" loading="lazy" className="h-16 w-auto object-contain hover:-translate-y-0.5 transition-transform duration-300" />
              </Link>
            ))}
          </div>
        </div>
        <div className="fade-up">
          <picture>
            <source media="(max-width: 768px)" srcSet="/images/lifestyle/lifestyle-juice-case-768.webp" type="image/webp" />
            <source srcSet="/images/lifestyle/lifestyle-juice-case-1200.webp" type="image/webp" />
            <img src="/images/lifestyle/lifestyle-juice-case-1200.jpg" fetchpriority="high"
              alt="Tiff Wellness Co. cold-pressed juice bottles on ice"
              width="600" height="848" className="w-full max-w-md mx-auto object-cover border-8 border-white shadow-[0_18px_50px_-20px_rgba(23,23,21,0.35)]" />
          </picture>
        </div>
      </div>
      </section>

      <section className="bg-white border-y border-[#EBD9D0]" data-testid="home-luxury-section">
        <div className="max-w-4xl mx-auto px-4 py-20 text-center">
          <h2 className="text-3xl sm:text-4xl lg:text-5xl">Health is the new luxury.</h2>
          <p className="mt-3 text-sm tracking-[0.2em] uppercase text-[#171715]/50 font-mono">health is wealth.</p>
          <p className="mt-6 text-sm md:text-base text-[#171715]/70 max-w-2xl mx-auto leading-relaxed">Build a life around what you're capable of — not just what you look like. Wellness extends beyond the mirror and the gym: it's how you fuel, how you recover, and how you show up every day.</p>
        </div>
      </section>

      <section className="max-w-6xl mx-auto px-4 py-14" id="juices">
        <p className="eyebrow mb-2">The lineup</p>
        <h2 className="text-2xl sm:text-3xl lg:text-4xl mb-8">Explore the Juice Lineup</h2>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {products.map((p) => <ProductCard key={p.slug} p={p} />)}
        </div>
      </section>

      <section className="bg-white border-y border-[#EBD9D0]">
        <div className="max-w-6xl mx-auto px-4 py-16">
          <h2 className="text-2xl sm:text-3xl lg:text-4xl mb-10">Build Your Stack</h2>
          <div className="grid md:grid-cols-3 gap-8">
            {STEPS.map((s) => (
              <div key={s.n}>
                <p className="font-mono text-sm text-[#A35847] mb-2">{s.n}</p>
                <h3 className="text-xl mb-2">{s.t}</h3>
                <p className="text-sm text-[#171715]/70 leading-relaxed">{s.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="max-w-6xl mx-auto px-4 py-16 grid md:grid-cols-2 gap-10">
        <div>
          <h2 className="text-2xl sm:text-3xl lg:text-4xl mb-4">Juice Subscriptions for Your Schedule</h2>
          <p className="text-sm text-[#171715]/75 leading-relaxed mb-4">Any number of bottles per pickup, every 7 or every 14 days. Functional juices price at $12 / $28 per 3 / $54 per 6 automatically (test reference prices — no subscription discount is offered until approved). One pickup per paid cycle. Skip, pause or cancel online anytime.</p>
          <Link to="/subscriptions" data-testid="home-subscribe-cta" className="btn-ink">See subscription plans</Link>
        </div>
        <div>
          <h2 className="text-2xl sm:text-3xl lg:text-4xl mb-4">Pickup in Dallas, Delivery by Arrangement</h2>
          <p className="text-sm text-[#171715]/75 leading-relaxed mb-4">Pick up at SandersFit Dallas or South Side on Lamar — Monday, Wednesday &amp; Thursday evenings, Tuesday &amp; Friday all day. Exact times are coordinated after your order is confirmed. Dallas–Fort Worth delivery is available by arrangement.</p>
          <Link to="/pickup" data-testid="home-pickup-cta" className="btn-ghost">Browse pickup locations</Link>
        </div>
      </section>

      <section className="bg-[#171715] text-[#FBF6F1]">
        <div className="max-w-6xl mx-auto px-4 py-16 grid md:grid-cols-[1fr_1.4fr] gap-10 items-center">
          <picture>
            <source srcSet="/images/tiff-wellness-community-original.webp" type="image/webp" />
            <img src="/images/tiff-wellness-community-original.jpg" alt="Runners and a Tiff. Wellness Co. juice table at a Dallas community track event" width="420" height="420" loading="lazy" className="w-full max-w-sm border border-white/10 object-cover" />
          </picture>
          <div>
            <p className="eyebrow !text-[#FBF6F1]/50 mb-3">Meet the founder</p>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl mb-4">Meet Tiff</h2>
            <p className="text-sm leading-relaxed text-[#FBF6F1]/80">Tiffany Cotto is a former professional NBA dancer for the San Antonio Spurs and Oklahoma City Thunder, and a competitive athlete. Years of movement and performance taught her discipline, strength, nutrition and recovery — and inspired a brand that unites fitness, nutrition and wellness in an accessible way. To Tiff, fitness means building a lifestyle that lets you feel strong, move confidently and perform at your best.</p>
            <p className="text-xs text-[#FBF6F1]/50 mt-3">From a Dallas community event.</p>
            <Link to="/our-story" data-testid="home-story-cta" className="btn-ghost !text-[#FBF6F1] !border-[#FBF6F1] mt-6">Read the story</Link>
          </div>
        </div>
      </section>

      <section className="max-w-6xl mx-auto px-4 py-16 grid md:grid-cols-[1.25fr_1fr] gap-10 items-center" data-testid="home-editorial-section">
        <EditorialIngredientsImage />
        <div>
          <p className="eyebrow mb-3">In the press room</p>
          <h2 className="text-2xl sm:text-3xl lg:text-4xl mb-4">Whole Ingredients, Pressed Fresh</h2>
          <p className="text-sm text-[#171715]/75 leading-relaxed mb-4">Pineapple, apple, cucumber, lime, watermelon, orange and beet — explore the fruit and vegetables featured in our juice menu.</p>
          <Link to="/ingredients" data-testid="home-editorial-ingredients-link" className="btn-ghost">Explore the ingredients</Link>
        </div>
      </section>

      <section className="blush-panel" data-testid="home-bulk-section">
        <div className="max-w-6xl mx-auto px-4 py-14 md:flex items-center justify-between gap-8">
          <div className="max-w-2xl">
            <p className="eyebrow mb-2">Events &amp; teams</p>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl mb-3">Bulk Orders for Events &amp; Teams</h2>
            <p className="text-sm text-[#171715]/75 leading-relaxed">36 bottles for $360 (reference pricing), made fresh for your date. Bulk orders need at least 48 hours' notice and confirmed pickup or delivery availability — send a request and we'll coordinate directly.</p>
          </div>
          <Link to="/build-your-stack#bulk" data-testid="home-bulk-cta" className="btn-ink mt-6 md:mt-0 shrink-0">Request a bulk order</Link>
        </div>
      </section>

      <section className="max-w-3xl mx-auto px-4 py-16">
        <h2 className="text-2xl sm:text-3xl lg:text-4xl mb-8">Your Questions, Answered</h2>
        <div className="space-y-3">
          {FAQS.map((f, i) => (
            <details key={i} data-testid={`home-faq-item-${i}`} className="card-paper p-4 group">
              <summary className="cursor-pointer font-medium text-sm min-h-[28px] list-none flex justify-between items-center">{f.q}<span className="text-[#A35847] group-open:rotate-45 transition-transform">+</span></summary>
              <p className="text-sm text-[#171715]/75 mt-3 leading-relaxed">{f.a}</p>
            </details>
          ))}
        </div>
        <Link to="/faq" className="inline-block mt-4 text-sm underline" data-testid="home-faq-all-link">All FAQs</Link>
      </section>

      <section className="bg-white border-t border-[#EBD9D0]">
        <div className="max-w-3xl mx-auto px-4 py-14 text-center">
          <h2 className="text-2xl sm:text-3xl mb-3">Join the list for pickup updates</h2>
          <p className="text-sm text-[#171715]/70 mb-6">Be first to hear when verified Dallas gym pickups go live.</p>
          <form onSubmit={signup} className="flex flex-col sm:flex-row gap-3 max-w-md mx-auto">
            <input data-testid="email-signup-input" type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
              placeholder="you@example.com" className="input-line flex-1" aria-label="Email address" />
            <button data-testid="email-signup-submit-button" className="btn-ink" disabled={busy}>{busy ? "Joining…" : "Join the list"}</button>
          </form>
          <label className="flex items-start gap-2 text-xs text-[#171715]/70 max-w-md mx-auto mt-3 text-left">
            <input data-testid="email-signup-consent-checkbox" type="checkbox" checked={optIn} onChange={(e) => setOptIn(e.target.checked)} className="mt-0.5 h-5 w-5" />
            <span>I agree to receive marketing email from tiff. wellness co. I can unsubscribe anytime.</span>
          </label>
        </div>
      </section>
    </div>
  );
}
